// Australian inventions Pokédex. Hand-curated for recognisability + accuracy.
// Each entry maps to a pixel-art ICON of the invention itself (not a person
// holding it) generated via PixelLab and pinned to its region of origin.
//
// Sources: National Museum of Australia, IP Australia, Engineers Australia,
// CSIRO archive, company-public histories. Each fact should be verifiable.

import type { StateCode } from './types';

export type InventionType =
  | 'health' | 'science' | 'consumer' | 'tech' | 'industrial' | 'agri' | 'defence' | 'culture';

export interface Invention {
  id: string;          // sprite key (also used as filename)
  name: string;        // headline name (kept short for card layout)
  year: number;        // earliest patent / first commercial release
  inventor: string;    // primary credited inventor (or company)
  origin: StateCode;   // state of origin
  type: InventionType; // for filtering / colour-coding
  blurb: string;       // 1-2 sentence story (Aussie voice, sharp)
  fact: string;        // shareable "did you know" line
  spritePrompt: string; // PixelLab generation prompt — describes the OBJECT itself
  // Accepted typings for the Smoko daily quiz. Should include the canonical
  // name (lower-cased), common nicknames, brand variants, and "what someone
  // might type when they recognise the sprite". All matched after normalising
  // (lowercase, strip punctuation/whitespace).
  aliases: string[];
  // The string used for the hangman-style fill-the-blanks puzzle. Should be
  // the most common/recognisable form of the name (often shorter than the
  // historical canonical `name`). All-uppercase. Spaces between words; only
  // A-Z letters become guessable blanks.
  puzzleName: string;
  // The riddle shown when the player clicks the locked card. First-person,
  // function-led, no geography/year giveaways. Aim for "smile of recognition"
  // difficulty — most Aussies should solve it in one read.
  riddle: string;
}

// Style tail for object sprites. Kept consistent so the Pokédex looks coherent.
const ICON =
  'pixel art icon of the object only, no people, ' +
  'centred on transparent background, 64x64 pixel art, ' +
  'Pokémon Diamond Pearl item style, soft shading, crisp pixels';

export const INVENTIONS: Invention[] = [
  {
    id: 'wifi',
    name: 'WiFi',
    year: 1992,
    inventor: 'CSIRO (John O\'Sullivan & team)',
    origin: 'NSW',
    type: 'tech',
    blurb: 'A radio astronomy team trying to detect exploding black holes accidentally cracked indoor wireless networking. CSIRO patented the maths. The world owes us about a billion dollars in royalties.',
    fact: 'WiFi was invented by Aussie radio astronomers, not engineers.',
    spritePrompt: `glowing teal wireless router with two upright antennas and three radio waves, ${ICON}`,
    aliases: ['wifi', 'wi-fi', 'wireless', 'wireless internet', 'wireless lan', '802.11'],
    puzzleName: 'WIFI',
    riddle: 'I connect you to the world. No strings attached.',
  },
  {
    id: 'cochlear',
    name: 'Cochlear Implant',
    year: 1978,
    inventor: 'Graeme Clark (Melbourne)',
    origin: 'VIC',
    type: 'health',
    blurb: 'A Melbourne professor whose deaf father inspired a decade of mucking around with electrodes and a snail-shell. Now over 700,000 people worldwide can hear because of it.',
    fact: 'Inspired by a sea-shell on a beach in 1977.',
    spritePrompt: `silver cochlear implant device shaped like a small ear hook with magnet disc and coiled electrode wire, ${ICON}`,
    aliases: ['cochlear', 'cochlear implant', 'bionic ear', 'hearing implant', 'hearing aid'],
    puzzleName: 'COCHLEAR',
    riddle: 'I\'m not the ear you were born with — but I\'ll let you hear.',
  },
  {
    id: 'blackbox',
    name: 'Black Box',
    year: 1958,
    inventor: 'David Warren (Melbourne)',
    origin: 'VIC',
    type: 'science',
    blurb: 'A chemist at the Aeronautical Research Lab who lost his dad in a plane crash designed a device to record what happened in the cockpit. Every airliner in the world now carries his idea.',
    fact: 'It was originally rejected by Australian aviators as "spying" — only the British took it seriously.',
    spritePrompt: `bright orange flight data recorder box with reflective stripes, antenna and dials, ${ICON}`,
    aliases: ['black box', 'blackbox', 'flight recorder', 'flight data recorder', 'fdr', 'cockpit voice recorder', 'cvr', 'plane recorder'],
    puzzleName: 'BLACK BOX',
    riddle: 'When planes fall from the sky, I survive to tell the story.',
  },
  {
    id: 'polymer-banknote',
    name: 'Polymer Banknote',
    year: 1988,
    inventor: 'CSIRO + Reserve Bank of Australia',
    origin: 'ACT',
    type: 'science',
    blurb: 'Tired of paper notes that ripped, faded, and were trivial to forge, Australia became the first country to print plastic money. Now used in 30+ countries from Canada to Vietnam.',
    fact: 'Australia\'s 1988 ten-dollar note was the world\'s first polymer banknote.',
    spritePrompt: `Australian blue plastic ten-dollar polymer banknote with see-through window and portrait, ${ICON}`,
    aliases: ['polymer banknote', 'polymer banknotes', 'plastic banknote', 'plastic banknotes', 'plastic money', 'polymer note', 'polymer notes', 'plastic note', 'plastic notes', 'polymer currency', 'plastic currency', 'plastic cash', 'polymer cash', 'plastic dollar', 'plastic dollars'],
    puzzleName: 'POLYMER NOTE',
    riddle: 'I survive the washing machine, the toddler, and the pub. I\'m money — but I\'m plastic.',
  },
  {
    id: 'vegemite',
    name: 'Vegemite',
    year: 1922,
    inventor: 'Cyril Callister (Melbourne)',
    origin: 'VIC',
    type: 'culture',
    blurb: 'A 26-year-old food chemist asked to invent a use for leftover brewer\'s yeast. The result remains either a national treasure or a war crime depending on who you ask.',
    fact: 'Originally flopped — only WWII army rations made it iconic.',
    spritePrompt: `iconic small black and yellow Vegemite jar with red lid and red Vegemite text, ${ICON}`,
    aliases: ['vegemite', 'vegimite', 'veg'],
    puzzleName: 'VEGEMITE',
    riddle: 'Black, salty, and divisive. Spread me thin if you can — and never thick.',
  },
  {
    id: 'hills-hoist',
    name: 'Hills Hoist',
    year: 1945,
    inventor: 'Lance Hill (Adelaide)',
    origin: 'SA',
    type: 'consumer',
    blurb: 'A returned soldier in Adelaide rigged up a rotary clothesline so his wife could fit more washing in a smaller backyard. Soon every Aussie back-yard had one.',
    fact: 'It\'s been listed as a National Treasure by the National Trust.',
    spritePrompt: `four-armed silver rotary clothesline pole with a few small towels hung on it, ${ICON}`,
    aliases: ['hills hoist', 'rotary clothesline', 'clothesline', 'washing line', 'rotary washing line', 'hills'],
    puzzleName: 'HILLS HOIST',
    riddle: 'Four arms wide, one pole tall — I spin in your backyard so your jocks dry in the breeze.',
  },
  {
    id: 'ute',
    name: 'The Ute',
    year: 1934,
    inventor: 'Lewis Bandt (Ford Australia)',
    origin: 'VIC',
    type: 'industrial',
    blurb: 'A Victorian farmer\'s wife wrote to Ford asking for a vehicle she could take to church on Sunday and pigs to market on Monday. Lewis Bandt drew the first one. The pickup truck was born.',
    fact: 'The American "El Camino" copied the Aussie ute by 25 years.',
    spritePrompt: `classic 1930s Ford coupe utility pickup truck in cream colour, side view, ${ICON}`,
    aliases: ['ute', 'the ute', 'utility', 'pickup', 'pickup truck', 'coupe utility', 'ford ute', 'tray back'],
    puzzleName: 'THE UTE',
    riddle: 'Half car, half truck. Born in 1934 when a farmer\'s wife asked Ford for one vehicle she could drive to church on Sunday and haul pigs in on Monday.',
  },
  {
    id: 'speedo',
    name: 'Speedo',
    year: 1928,
    inventor: 'Alexander MacRae (Sydney)',
    origin: 'NSW',
    type: 'consumer',
    blurb: 'A Scottish migrant\'s underwear factory in Bondi started making swimwear. Sixty Olympic golds later, "speedos" became a noun in every English-speaking country.',
    fact: 'The original company was called MacRae Hosiery Manufacturers.',
    spritePrompt: `small red Speedo brand swim brief with white logo, ${ICON}`,
    aliases: ['speedo', 'speedos', 'budgie smugglers', 'budgie smuggler', 'swim briefs', 'swimwear', 'swimsuit', 'swim brief'],
    puzzleName: 'SPEEDOS',
    riddle: 'A 1928 Bondi swim brief that became a brand name everywhere — though Aussies still call them budgie smugglers.',
  },
  {
    id: 'wine-cask',
    name: 'Goon Bag',
    year: 1965,
    inventor: 'Thomas Angove (Renmark, SA)',
    origin: 'SA',
    type: 'culture',
    blurb: 'A South Australian winemaker invented the bag-in-box so wine could survive a road trip. Forever ruining university dorms in the process.',
    fact: 'Patent filed by Angove\'s in May 1965.',
    spritePrompt: `silver foil bag-in-box wine cask sitting on its cardboard outer with spout out, ${ICON}`,
    aliases: ['goon', 'goon bag', 'goonbag', 'goon sack', 'goonsack', 'wine cask', 'cask wine', 'box wine', 'bag in box', 'bib', 'wine box', 'goon of fortune'],
    puzzleName: 'GOON BAG',
    riddle: 'Silver bladder full of cheap plonk, hidden in a cardboard box. The uni student\'s best mate.',
  },
  {
    id: 'tim-tam',
    name: 'Tim Tam',
    year: 1964,
    inventor: 'Ross Arnott (Sydney)',
    origin: 'NSW',
    type: 'culture',
    blurb: 'Named after the 1958 Kentucky Derby winner because Ross Arnott liked horse racing. Australia\'s most exported biscuit, source of the legendary "Tim Tam slam".',
    fact: 'Over 45 million packs are sold each year.',
    spritePrompt: `single dark chocolate-coated rectangular Tim Tam biscuit with one bite taken out of one corner, ${ICON}`,
    aliases: ['tim tam', 'tim tams', 'timtam', 'timtams'],
    puzzleName: 'TIM TAM',
    riddle: 'Bite each end. Dunk in tea. Suck. Try not to drown.',
  },
  {
    id: 'spray-skin',
    name: 'Spray-on Skin',
    year: 1999,
    inventor: 'Fiona Wood (Perth)',
    origin: 'WA',
    type: 'health',
    blurb: 'A Perth burns surgeon developed a way to take a postage-stamp\'s worth of skin and spray it across burn wounds. Saved hundreds of lives after the 2002 Bali bombings.',
    fact: 'Wood was Australian of the Year in 2005.',
    spritePrompt: `medical white spray-on skin canister with nozzle emitting a fine pink mist, ${ICON}`,
    aliases: ['spray on skin', 'spray-on skin', 'spray skin', 'recell', 'skin spray', 'burn spray'],
    puzzleName: 'SPRAY ON SKIN',
    riddle: 'Take a stamp-sized patch of you, mist it across a burn. New skin in days, not months.',
  },
  {
    id: 'pacemaker',
    name: 'Pacemaker',
    year: 1926,
    inventor: 'Mark Lidwill & Edgar Booth (Sydney)',
    origin: 'NSW',
    type: 'health',
    blurb: 'A Sydney anaesthetist and physicist built one of the first electrical heart-resuscitation devices. Used to revive a still-born baby at the Royal Crown Street Women\'s Hospital.',
    fact: 'Lidwill refused to publish the device for fear of religious controversy.',
    spritePrompt: `vintage 1920s wooden electrical heart resuscitation device with brass dials, electrodes and wires, ${ICON}`,
    aliases: ['pacemaker', 'pace maker', 'heart pacemaker', 'heart pace maker', 'cardiac pacemaker', 'cardiac pace maker', 'electric heart'],
    puzzleName: 'PACEMAKER',
    riddle: 'When your heart forgets the beat, I\'m the one tapping out the rhythm.',
  },
  {
    id: 'plastic-lens',
    name: 'Plastic Lenses',
    year: 1960,
    inventor: 'SOLA Optical (Adelaide)',
    origin: 'SA',
    type: 'consumer',
    blurb: 'An Adelaide outfit cracked the chemistry of pouring plastic into eyeglass lenses, replacing heavy shatter-prone glass. Most of the world\'s glasses are now Aussie-formula plastic.',
    fact: 'SOLA stood for "Scientific Optical Laboratories of Australia".',
    spritePrompt: `pair of black-frame eyeglasses with thick plastic lenses, front view, ${ICON}`,
    aliases: ['plastic lens', 'plastic lenses', 'plastic lense', 'plastic lenses', 'plastic glasses', 'plastic eyeglasses', 'sola', 'plastic spectacles', 'plastic eyeglass lens', 'eyeglass lens', 'eyeglass lenses', 'eyeglass lense'],
    puzzleName: 'PLASTIC LENS',
    riddle: 'Sharp vision, no weight on your nose. Glass got the boot.',
  },
  {
    id: 'refrigeration',
    name: 'Refrigeration',
    year: 1856,
    inventor: 'James Harrison (Geelong)',
    origin: 'VIC',
    type: 'industrial',
    blurb: 'A Geelong newspaper editor and engineer built the world\'s first practical vapour-compression refrigerator. Made shipping frozen Aussie meat to Britain possible.',
    fact: 'Beat the American Carrier patent by half a century.',
    spritePrompt: `Victorian-era brass and copper mechanical refrigeration machine with pressure gauges and pipes, ${ICON}`,
    aliases: ['refrigeration', 'fridge', 'refrigerator', 'freezer', 'ice machine', 'cold storage', 'cooling machine'],
    puzzleName: 'FRIDGE',
    riddle: 'Cold beer. Fresh meat. Half the world\'s kitchens owe me one.',
  },
  {
    id: 'stump-jump',
    name: 'Stump-Jump Plough',
    year: 1876,
    inventor: 'Richard Bowyer Smith (SA)',
    origin: 'SA',
    type: 'agri',
    blurb: 'A South Australian farmer\'s plough that could ride over tree stumps instead of breaking on them. Unlocked millions of hectares of mallee country for grain farming.',
    fact: 'Tested on the Yorke Peninsula where stumps were everywhere.',
    spritePrompt: `wooden farm plough with three hinged steel blades on wheels, side view, ${ICON}`,
    aliases: ['stump jump plough', 'stump-jump plough', 'stump jump plow', 'stump-jump plow', 'stump jump', 'plough', 'plow', 'stump jumper'],
    puzzleName: 'STUMP PLOUGH',
    riddle: 'Other ploughs broke on tree stumps. I just hop right over them.',
  },
  {
    id: 'combine-harvester',
    name: 'Sunshine Harvester',
    year: 1885,
    inventor: 'Hugh Victor McKay (VIC)',
    origin: 'VIC',
    type: 'agri',
    blurb: 'A 19-year-old Victorian farm boy combined harvesting, threshing and winnowing into one machine. The "Sunshine" became the world\'s most popular harvester.',
    fact: 'McKay\'s factory in Sunshine VIC employed 3,000 workers at peak.',
    spritePrompt: `vintage red mechanical wheat stripper harvester with reels and wheels, side view, ${ICON}`,
    aliases: ['sunshine harvester', 'harvester', 'combine harvester', 'combine', 'wheat harvester', 'mckay harvester', 'wheat stripper', 'stripper'],
    puzzleName: 'HARVESTER',
    riddle: 'Cut, thresh, winnow — three farm jobs, one big red machine.',
  },
  {
    id: 'holden-48-215',
    name: 'Holden 48-215',
    year: 1948,
    inventor: 'General Motors-Holden (Melbourne)',
    origin: 'VIC',
    type: 'industrial',
    blurb: 'Australia\'s first wholly-designed and built car. Prime Minister Ben Chifley unveiled it with the line "she\'s a beauty". For 50 years, "Holden" meant Aussie pride.',
    fact: 'Chifley\'s actual quote was apparently more colourful.',
    spritePrompt: `1948 cream-coloured Holden FX sedan car with curved fenders, side view, ${ICON}`,
    aliases: ['holden', 'holden 48-215', 'fx holden', 'holden fx', 'fx', '48-215', 'holden sedan', 'first holden', 'aussie car'],
    puzzleName: 'HOLDEN',
    riddle: 'PM Ben Chifley unveiled me with the line "she\'s a beauty". Australia\'s first proper car.',
  },
  {
    id: 'plane-slide',
    name: 'Plane Slide',
    year: 1965,
    inventor: 'Jack Grant (Qantas)',
    origin: 'NSW',
    type: 'science',
    blurb: 'A Qantas safety officer designed the inflatable yellow slide that pops out of every modern airliner door. Has saved lives on every continent except Antarctica.',
    fact: 'Tested with Qantas employees as the test crew.',
    spritePrompt: `yellow inflatable airliner emergency escape slide deployed at an angle, ${ICON}`,
    aliases: ['plane slide', 'escape slide', 'emergency slide', 'evacuation slide', 'inflatable slide', 'airplane slide', 'aircraft slide', 'safety slide', 'aeroplane slide', 'inflatable escape slide'],
    puzzleName: 'PLANE SLIDE',
    riddle: 'Door pops, I inflate, you slide. Yellow, bouncy, your fastest route to the tarmac.',
  },
  {
    id: 'solar-hot-water',
    name: 'Solar Hot Water',
    year: 1953,
    inventor: 'CSIRO (Roger Morse)',
    origin: 'ACT',
    type: 'science',
    blurb: 'CSIRO designed the flat-plate solar water heater that\'s become standard on Aussie roofs. Free hot showers, courtesy of physics.',
    fact: 'Australia has more solar hot-water installations per capita than anywhere on earth.',
    spritePrompt: `white rooftop solar hot water collector panel with copper pipes and a horizontal storage tank above it, ${ICON}`,
    aliases: ['solar hot water', 'solar water heater', 'solar heater', 'solar water', 'solar panel', 'rooftop solar'],
    puzzleName: 'SOLAR HOT WATER',
    riddle: 'Free showers, courtesy of the sun and a dark panel on your roof.',
  },
  {
    id: 'permanent-press',
    name: 'Si-Ro-Set',
    year: 1957,
    inventor: 'CSIRO (Arthur Farnworth)',
    origin: 'VIC',
    type: 'consumer',
    blurb: 'CSIRO chemists figured out how to permanently set a crease into wool trousers. Every man\'s suit pant since owes Aussie sheep a thank you.',
    fact: 'Licensed to over 50 countries by 1965.',
    spritePrompt: `pair of grey wool trousers on a wooden hanger with a sharp ironed crease down each leg, ${ICON}`,
    aliases: ['si-ro-set', 'siroset', 'si ro set', 'permanent press', 'permanent crease', 'wool crease', 'pleated trousers'],
    puzzleName: 'PERMANENT PRESS',
    riddle: 'The crease in your suit pants used to die by lunch. I made it last forever.',
  },
  {
    id: 'atlassian-jira',
    name: 'Atlassian',
    year: 2002,
    inventor: 'Mike Cannon-Brookes & Scott Farquhar (Sydney)',
    origin: 'NSW',
    type: 'tech',
    blurb: 'Two UNSW grads built developer tools out of a Sydney garage. Now Atlassian is worth more than Telstra and run from a tower the locals call "Atlassian Central".',
    fact: 'They funded the company on credit cards for the first three years.',
    spritePrompt: `glowing blue laptop computer screen showing a stylised three-column Kanban board with sticky notes, ${ICON}`,
    aliases: ['atlassian', 'jira', 'confluence', 'trello', 'atlassian jira'],
    puzzleName: 'ATLASSIAN',
    riddle: 'Where developers track their bugs and curse at sticky notes.',
  },
  {
    id: 'canva',
    name: 'Canva',
    year: 2013,
    inventor: 'Melanie Perkins, Cliff Obrecht & Cameron Adams (Perth → Sydney)',
    origin: 'NSW',
    type: 'tech',
    blurb: 'A Perth uni student got fed up with InDesign and built drag-and-drop design for everyone else. Worth $40bn and counting, with most of its early team still in Australia.',
    fact: 'Perkins pitched 100+ VCs before getting yes.',
    spritePrompt: `colourful design app icon showing a paintbrush, palette and rainbow gradient template, ${ICON}`,
    aliases: ['canva'],
    puzzleName: 'CANVA',
    riddle: 'I made graphic design something your nan can do on her phone.',
  },
  {
    id: 'resmed-cpap',
    name: 'ResMed CPAP',
    year: 1989,
    inventor: 'Colin Sullivan (University of Sydney)',
    origin: 'NSW',
    type: 'health',
    blurb: 'A Sydney respiratory researcher invented the modern continuous-positive-airway-pressure machine for sleep apnea. ResMed now supplies most of the planet\'s CPAPs.',
    fact: 'First prototype used a vacuum cleaner motor.',
    spritePrompt: `white medical CPAP breathing mask with translucent plastic and a coiled grey hose attached, ${ICON}`,
    aliases: ['cpap', 'resmed', 'resmed cpap', 'cpap machine', 'sleep apnea machine', 'breathing machine', 'sleep apnoea'],
    puzzleName: 'CPAP MACHINE',
    riddle: 'Snore like a chainsaw? I\'m the mask that lets your partner sleep.',
  },
  {
    id: 'afterpay',
    name: 'Afterpay',
    year: 2014,
    inventor: 'Nick Molnar & Anthony Eisen (Sydney)',
    origin: 'NSW',
    type: 'tech',
    blurb: 'Two Sydney financiers turned "buy now, pay later" into a global retail standard. Sold to Block (Square) for AU$39bn — the largest Aussie acquisition ever.',
    fact: 'Molnar was 24 when the company listed on the ASX.',
    spritePrompt: `mint-green shopping bag icon with a four-quartered payment circle hovering above it, ${ICON}`,
    aliases: ['afterpay', 'after pay', 'bnpl', 'buy now pay later', 'pay later'],
    puzzleName: 'AFTERPAY',
    riddle: 'Buy now. Pay in four. She\'ll be right.',
  },
];
